import { Component, type ErrorInfo, type ReactNode } from "react";
import type { ConvexReactClient } from "convex/react";
import { api } from "../../convex/_generated/api";

let client: ConvexReactClient | null = null;
const seen = new Set<string>();

/** Send a browser error to the server (deduplicated per page load). Never throws. */
export function reportError(kind: string, e: unknown, componentStack?: string) {
  const err = e instanceof Error ? e : new Error(String(e));
  const key = `${kind}:${err.message}`;
  if (!client || seen.has(key) || seen.size > 20) return;
  seen.add(key);
  void client
    .mutation(api.telemetry.reportClientError, {
      kind,
      message: err.message || String(e),
      stack: err.stack,
      componentStack,
      url: location.href,
      userAgent: navigator.userAgent,
    })
    .catch(() => {});
}

export function installErrorReporting(c: ConvexReactClient) {
  client = c;
  window.addEventListener("vite:preloadError", (ev) => {
    if (reloadIfStale((ev as unknown as { payload: unknown }).payload)) ev.preventDefault();
  });
  window.addEventListener("error", (ev) => reportError("window.error", ev.error ?? ev.message));
  window.addEventListener("unhandledrejection", (ev) => {
    // Mutation failures are already shown to the user as toasts; only report real crashes.
    const r = ev.reason;
    if (r && typeof r === "object" && "data" in r) return;
    reportError("unhandledrejection", r);
  });
}

const STALE = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Expected a JavaScript/i;

/** A chunk from an older deploy failed to load: reload once to pick up the current version. */
export function reloadIfStale(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : String(e);
  if (!STALE.test(msg)) return false;
  const last = Number(sessionStorage.getItem("evm-stale-reload") ?? 0);
  if (Date.now() - last < 60_000) return false; // don't loop
  sessionStorage.setItem("evm-stale-reload", String(Date.now()));
  location.reload();
  return true;
}

/** Keeps one broken screen from blanking the whole app, and reports the crash. */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError("render", error, info.componentStack ?? undefined);
    reloadIfStale(error);
  }
  componentDidUpdate(prev: { resetKey?: string }) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" className="m-6 max-w-2xl rounded-lg border border-red-200 bg-red-50 p-5">
        <h2 className="font-semibold text-red-800">Something broke on this screen</h2>
        <p className="mt-1 text-sm text-red-700">The error was reported to us automatically. Your saved data is fine. Reload to continue, or go to another page from the menu.</p>
        <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-surface p-2 text-xs text-slate-600">{this.state.error.message}</pre>
        <button className="mt-3 rounded-md bg-red-600 px-3 py-1.5 text-sm text-white" onClick={() => location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}
